'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { Divider } from 'components/divider'
import { ChevronDownIcon, ChevronUpIcon } from 'primitives/icons'

import { SidePanelButton } from './side-panel-button'
import { useConfig } from '../../_hooks/use-config'
import type { Variant } from '../config'
import { InlineStaticVariant } from '@repo/web-ui/components/static-variants-renderer/inline/inline-static-variant'
import { COUNTRY_COOKIE_NAME } from 'constants/cookies'
import { CurrencyCode } from 'codegen/types'

export function FloatingSidePanel({
  variant,
  loading = false,
}: {
  variant: Variant
  loading?: boolean
}) {
  const t = useTranslations('pages.b.side_panel')

  const [isOpen, setIsOpen] = React.useState(false)

  const { promoCode, saving, subtotal, total, currency } = useConfig(variant)

  const formattedSubtotal = `${currency?.symbol}${subtotal}`
  const formattedTotal = `${currency?.symbol}${total}`
  const formattedSaving = `${currency?.symbol}${saving}`

  const zeroPrice = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency?.code || CurrencyCode.Usd,
  }).format(0)

  return (
    <div className="bg-service-white flex w-full flex-col border-t border-[#e3e1e9] px-[15px] py-[10px]">
      {isOpen ? (
        <>
          <div className="mb-[10px] flex w-full justify-between">
            <p className="text-14 tracking-[0.3px]">{t('subtotal')}</p>
            <p className="text-14 font-bold">{!loading ? formattedSubtotal : ''}</p>
          </div>
          {promoCode.length > 0 ? (
            <>
              <div className="mb-2 flex w-full justify-between">
                <p className="text-14 tracking-[0.3px]">{t('promo')}</p>
              </div>
              <div className="mb-[10px] flex w-full justify-between">
                <div className="text-12 rounded bg-[#e4e5ec] px-2 py-1 font-semibold">
                  {promoCode}
                </div>
                <p className="text-14 font-bold">- {currency && !loading ? formattedSaving : ''}</p>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {promoCode.length > 0 ? (
        <p className="text-14 text-secondary-500 flex w-full justify-end tracking-[0.3px]">
          <InlineStaticVariant
            conditions={[
              {
                type: 'cookie',
                cookieName: COUNTRY_COOKIE_NAME,
                expectedValue: 'US',
                operator: 'EQUALS',
              },
              {
                type: 'feature-flag',
                featureName: 'GROW-1258-us-sales-tax-experiment-extension',
                expectedValue: 1,
              },
            ]}
            getVariant={({ conditionsMet }) => {
              return conditionsMet
                ? t('sales_tax_variant_1')
                : t.rich('saving', {
                    price: formattedSaving,
                  })
            }}
          />
        </p>
      ) : null}
      {isOpen ? (
        <Divider className="mb-5 mt-[10px] bg-[#e3e1e9]" />
      ) : (
        <Divider className="my-[10px]" />
      )}
      <div className="mb-[10px] flex w-full items-center justify-between">
        <div className="flex items-center gap-[10px]">
          <p className="text-14 font-bold uppercase tracking-[0.3px]">{t('total')}</p>
          {isOpen ? (
            <ChevronDownIcon
              onClick={() => {
                setIsOpen(prev => !prev)
              }}
              className="cursor-pointer"
              width="20px"
              height="20px"
            />
          ) : (
            <ChevronUpIcon
              onClick={() => {
                setIsOpen(prev => !prev)
              }}
              className="cursor-pointer"
              width="20px"
              height="20px"
            />
          )}
        </div>
        <div className="flex flex-col items-center">
          <p className="text-[24px] font-bold leading-6">
            {!loading ? formattedTotal : <>&nbsp;</>}
          </p>
        </div>
      </div>
      <SidePanelButton
        label={variant === 'freeTrialYearly' ? t('cta_variant', { price: zeroPrice }) : t('cta')}
        variant={variant}
      />
    </div>
  )
}
